import type { UI } from '@/v2/types';
import { Flex } from '@qga/roo-ui/components';

import PayWithButtonGroup from '@/components/PayWithButtonGroup';
import CalendarInput from '@/v2/components/pages/PackageView/components/CalendarInput';
import OtherOption from '@/v2/components/pages/PackageView/components/RoomOptions/components/SearchPanel/components/OtherOption';
import SelectedRoomPanel from '@/v2/components/pages/PackageView/components/RoomOptions/components/SearchPanel/components/SelectedRoomPanel';
import TravellersInput from '@/v2/components/pages/PackageView/components/TravellersInput';
import { useRoomOptionsData } from '@/v2/components/pages/PackageView/contexts/RoomOptionsContext';
import { useSearchPanelControls } from '@/v2/components/pages/PackageView/contexts/SearchPanelControlsContext';
import { useSearchPanelData } from '@/v2/components/pages/PackageView/contexts/SearchPanelDataContext';
import useHandleReserve from '@/v2/components/pages/PackageView/hooks/useHandleReserve';
import { useSearchPanelMessaging } from '@/v2/components/pages/PackageView/hooks/useSearchPanelMessaging';
import { usePackagePageV3 } from '@/v2/hooks/featureFlags/packagePageV3/usePackagePageV3';
import CampaignMessage from '../../../CampaignMessage';
import AlternativeOffer from './components/AlternativeOffer';
import CtaButton from './components/CtaButton';
import PackageOptionDetails from './components/PackageOptionDetails';

export type SearchPanelProps = {
  campaign: UI.Campaign | null;
};

const SearchPanel = ({ campaign }: SearchPanelProps) => {
  const isPackagePageV3Enabled = usePackagePageV3();
  const {
    roomOptions,
    selectedRoomOption,
    selectedPackageOption,
    isPreSearch,
    isLoading,
  } = useRoomOptionsData() || {};

  const { campaignMessage, procuredOfferDatesMessage } =
    useSearchPanelMessaging({ campaign });

  // Field controls
  const { openCalendar, isTravellersOpen, isCalendarOpen } =
    useSearchPanelControls();
  const { isDisabled } = useSearchPanelData();
  const isChangingTravelInfo = isTravellersOpen || isCalendarOpen;

  // Handle reserve
  const handleReserve = useHandleReserve();

  const isPackageDetailsPresearchEnabled =
    isPackagePageV3Enabled &&
    isPreSearch &&
    selectedPackageOption &&
    !isLoading;

  const hasMatchingOffer = !!roomOptions?.main?.some(
    (o) => o.roomType.id === selectedRoomOption?.roomType.id && o.matchingOffer,
  );

  return (
    <>
      <Flex
        display={['none', null, 'flex']}
        borderRadius="4px"
        flexDirection="column"
        maxWidth="25.75rem"
        bg="white"
        width="100%"
      >
        <CampaignMessage text={campaignMessage} />
        <Flex gap="16px" flexDirection="column" p="6">
          <CalendarInput campaignMessage={procuredOfferDatesMessage} />
          <TravellersInput />
          {/* @ts-ignore - PropTypes don't include styled-system */}
          <PayWithButtonGroup
            mt="2"
            mb="4"
            disabled={isDisabled}
            pointsPlusPayEnabled
          />
          <CtaButton
            hasSelection={hasMatchingOffer}
            onReserve={isChangingTravelInfo ? () => {} : handleReserve}
            onCheckAvailability={isChangingTravelInfo ? () => {} : openCalendar}
            isDisabled={isDisabled}
          />
          {isPackageDetailsPresearchEnabled && (
            <PackageOptionDetails
              selectedPackageOption={selectedPackageOption}
            />
          )}
          {selectedRoomOption && !isLoading && (
            <SelectedRoomPanel
              selectedPackageOption={selectedPackageOption}
              selectedRoomOption={selectedRoomOption}
            />
          )}
          <OtherOption />
          {!selectedRoomOption && !isPreSearch && !isLoading && (
            <AlternativeOffer />
          )}
        </Flex>
      </Flex>
    </>
  );
};

export default SearchPanel;
